import React, { useState, useEffect } from 'react'
import {
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	Collapse,
	Icon,
	IconButton,
	ListItemText,
	makeStyles,
	MenuItem,
	Select,
	TextField
} from '@material-ui/core'
import { ArrowForwardIosOutlined, ArrowBackIosOutlined } from '@material-ui/icons'
import { ExpandMoreOutlined } from '@material-ui/icons'
import { DeletableChip } from '../../Misc/DeletableChip'
import { DangerChip } from '../../Misc/DangerChip'
import { KeyboardDatePicker } from '@material-ui/pickers'
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import useConstants from '../../../hooks/useConstants'

const useStyles = makeStyles((theme) => ({
	header: {
		padding: '13px 24px',
		userSelect: 'none',
		cursor: 'pointer',
		'& .MuiCardHeader-action': {
			margin: 0
		}
	},
	grid: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		'&:last-child': {
			marginBottom: 0
		}
	},
	gridDouble: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			}
		}
	},
	gridTriple: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '274px !important'
			},
			'& .MuiFormControl-root': {
				minWidth: '272px !important'
			}
		}
	},

	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		}),
		padding: 0
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: '6px',
		backgroundColor: 'transparent !important'
	},
	dangerChip: {
		color: '#D02C2C'
	},
	dangerBackground: {
		backgroundColor: 'rgba(208, 44, 44, 0.1)'
	},
	footerButton: {
		fontFamily: 'Nunito',
		fontWeight: '800',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		}
	},
	fieldWrapper: {
		'& .MuiTextField-root': {
			minWidth: '274px !important'
		}
	}
}))

const PetServicesCard = (props) => {
	const classes = useStyles()
	const { tags } = useConstants()

	let { pet, setPet } = props

	const [isExpanded, setIsExpanded] = useState(false)
	const [petTags, setPetTags] = useState([])
	const [isOpen, setIsOpen] = useState(false)

	const temperamentTestStatusOptions = ['Unscheduled', 'Scheduled', 'Passed', 'Failed']
	const nightProgramRestrictions = ['Luxury', 'Kennel-Free']
	const services = ['Bar & Park', 'Daycare & Boarding', 'Home Services', 'Grooming', 'Training']

	useEffect(() => {
		if (tags && pet.tags && pet.tags.health && pet.tags.health.length) {
			let h = tags.health.filter((tag) => {
				return pet.tags.health.findIndex((pt) => pt._id === tag._id) !== -1
			})

			setPetTags(h)
		} else {
			setPetTags([])
		}
	}, [tags, pet])

	const updatePet = (field, value) => {
		setPet({ ...pet, [field]: value })
	}

	const updateTempServiceRestriction = (value) => {
		setPet({ ...pet, temporary_service_restrictions: value })
	}

	const removeTag = (value) => {}

	return (
		<Card>
			<CardHeader
				title="Services"
				className={classes.header}
				onClick={() => setIsExpanded(!isExpanded)}
				action={
					<IconButton
						className={clsx(classes.expand, {
							[classes.expandOpen]: isExpanded
						})}
						onClick={() => setIsExpanded(!isExpanded)}
						aria-expanded={isExpanded}
						aria-label="show more">
						<ExpandMoreOutlined style={{ fill: '#777679' }} />
					</IconButton>
				}
			/>
			<Collapse in={isExpanded} timeout="auto" unmountOnExit>
				<CardContent>
					<div className={classes.gridDouble}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								select
								label="Compatibility Session Status"
								variant="filled"
								value={pet.temperament_test_status}
								disabled={!pet.active}
								onChange={(e) => setPet({ ...pet, temperament_test_status: e.target.value })}>
								{temperamentTestStatusOptions.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</TextField>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<div className={classes.fieldWrapper}>
								<Autocomplete
									multiple
									getOptionLabel={(option) => option || ''}
									disableCloseOnSelect
									size="small"
									id="health-tags"
									className={classes.autoComplete}
									options={nightProgramRestrictions}
									value={
										pet?.night_program_restrictions && pet.night_program_restrictions.length
											? pet.night_program_restrictions
											: []
									}
									onChange={(e, v) => setPet({ ...pet, night_program_restrictions: v })}
									onClose={(e) => {
										e.target.value = ''
									}}
									disabled={!pet.active}
									renderTags={(selected) =>
										selected.map((value) => (
											<DangerChip
												key={value}
												label={value}
												color="primary"
												classes={{
													label: classes.dangerChip,
													icon: classes.dangerChip,
													colorPrimary: classes.dangerBackground
												}}
												{...{
													onDelete: pet.active
														? () => {
																let update = pet.night_program_restrictions
																	? pet.night_program_restrictions.filter(
																			(t) => t !== value
																	  )
																	: []

																setPet({
																	...pet,
																	night_program_restrictions: update
																})
														  }
														: null
												}}
											/>
										))
									}
									renderOption={(option, { selected }) => (
										<React.Fragment>
											<Checkbox checked={selected} />
											<ListItemText
												style={{ textTransform: 'capitalize' }}
												primary={option}
											/>
										</React.Fragment>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											variant="filled"
											fullWidth
											label="Pet Care Program Restrictions"
										/>
									)}
								/>
							</div>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Autocomplete
								multiple
								getOptionLabel={(option) => option || ''}
								disableCloseOnSelect
								size="small"
								id="health-tags"
								className={classes.autoComplete}
								options={services}
								value={
									pet?.temporary_service_restrictions &&
									pet.temporary_service_restrictions.length
										? pet.temporary_service_restrictions
										: []
								}
								onChange={(e, v) => setPet({ ...pet, temporary_service_restrictions: v })}
								onClose={(e) => {
									e.target.value = ''
								}}
								disabled={!pet.active}
								renderTags={(selected) =>
									selected.map((value) => (
										<DangerChip
											key={value._id}
											label={value}
											color="primary"
											classes={{
												label: classes.dangerChip,
												icon: classes.dangerChip,
												colorPrimary: classes.dangerBackground
											}}
											{...{
												onDelete: pet.active
													? () => {
															let update = pet.temporary_service_restrictions.filter(
																(t) => t !== value
															)
															setPet({
																...pet,
																temporary_service_restrictions: update
															})
													  }
													: null
											}}
										/>
									))
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText style={{ textTransform: 'capitalize' }} primary={option} />
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="filled"
										fullWidth
										label="Temporary Service Restriction"
									/>
								)}
							/>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Autocomplete
								multiple
								getOptionLabel={(option) => option || ''}
								disableCloseOnSelect
								size="small"
								id="health-tags"
								className={classes.autoComplete}
								options={services}
								value={
									pet?.permanent_service_restrictions &&
									pet.permanent_service_restrictions.length
										? pet.permanent_service_restrictions
										: []
								}
								onChange={(e, v) => setPet({ ...pet, permanent_service_restrictions: v })}
								onClose={(e) => {
									e.target.value = ''
								}}
								disabled={!pet.active}
								renderTags={(selected) =>
									selected.map((value) => (
										<DeletableChip
											key={value._id}
											label={value}
											{...{
												onDelete: pet.active
													? () => {
															let update = pet.permanent_service_restrictions.filter(
																(t) => t !== value
															)
															setPet({
																...pet,
																permanent_service_restrictions: update
															})
													  }
													: null
											}}
										/>
									))
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText style={{ textTransform: 'capitalize' }} primary={option} />
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="filled"
										fullWidth
										label="Permanent Service Restriction"
									/>
								)}
							/>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<KeyboardDatePicker
								disableToolbar
								open={isOpen}
								onOpen={() => setIsOpen(true)}
								onClose={() => setIsOpen(false)}
								disabled={!pet.active}
								InputAdornmentProps={{
									position: 'end',
									component: () => (
										<IconButton disabled={!pet.active} onClick={() => setIsOpen(true)}>
											<Icon
												className="fas fa-calendar"
												style={{
													fontSize: '1.325rem'
												}}
											/>
										</IconButton>
									)
								}}
								variant="inline"
								format="MM/DD/YYYY"
								id={`date-start-date`}
								label={`Profile Verification Date`}
								inputVariant="filled"
								value={pet.profile_verification_date || null}
								onChange={(date) => {
									// console.log("date", date);
									setPet({ ...pet, profile_verification_date: date.toDate() })
								}}
								className={classes.datePicker}
								rightArrowIcon={
									<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
								}
								leftArrowIcon={<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />}
								KeyboardButtonProps={{
									'aria-label': 'Start Date',
									className: classes.calendarButton
								}}
							/>
						</div>
					</div>
				</CardContent>
			</Collapse>
		</Card>
	)
}

export default PetServicesCard
